const FuzzySearch = {
  searchPageState: null,
  cardCache: new Map(),
  indexBuildPromise: null,
  partialIndexCallbacks: [],
  INDEX_CACHE_KEY: "autel_fuzzy_product_index_v1",
  INDEX_CACHE_TTL_MS: 30 * 60 * 1000,
  HANDLE_DELIMITER: " :: ",

  paginationIcons: {
    previous:
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon icon-chevron-left" aria-hidden="true"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 6l-6 6l6 6" /></svg>',
    next: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon icon-chevron-right" aria-hidden="true"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>',
  },

  buildSectionUrl(query, format, page, handles = []) {
    const sectionId = format === "grid" ? "fuzzy-search-grid" : "fuzzy-search-predictive";
    const searchQuery =
      handles.length > 0 ? `${query}${this.HANDLE_DELIMITER}${handles.join(",")}` : query;
    const params = new URLSearchParams({
      q: searchQuery,
      section_id: sectionId,
    });

    if (!handles.length) {
      params.set("page", String(page));
    }

    return `${routes.search_url}?${params.toString()}`;
  },

  buildPageUrl(query, page) {
    const currentParams = new URLSearchParams(window.location.search);
    const params = new URLSearchParams();

    params.set("q", query);
    params.set("options[prefix]", currentParams.get("options[prefix]") || "last");

    const sortBy = currentParams.get("sort_by");
    if (sortBy) {
      params.set("sort_by", sortBy);
    }

    if (page > 1) {
      params.set("page", String(page));
    }

    return `${routes.search_url}?${params.toString()}`;
  },

  getProductsJsonBaseUrl() {
    return `${window.routes.all_products_url.replace(/\/$/, "")}/products.json`;
  },

  parseSectionHtml(text) {
    return new DOMParser().parseFromString(text, "text/html");
  },

  getExistingHandles(container) {
    const handles = new Set();

    container.querySelectorAll("[data-product-handle], [data-fuzzy-product-handle]").forEach((element) => {
      const handle = element.dataset.productHandle || element.dataset.fuzzyProductHandle;
      if (handle) {
        handles.add(handle);
      }
    });

    container.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const match = link.getAttribute("href")?.match(/\/products\/([^/?#]+)/);
      if (match?.[1]) {
        handles.add(match[1]);
      }
    });

    return handles;
  },

  buildSearchBlob(product) {
    const tags = Array.isArray(product.tags)
      ? product.tags.join(" ")
      : String(product.tags || "");
    const variantText = (product.variants || [])
      .map((variant) => `${variant.sku || ""} ${variant.title || ""}`)
      .join(" ");

    return [product.title, product.vendor, product.handle, product.product_type, tags, variantText]
      .join(" ")
      .toLowerCase();
  },

  productMatchesQuery(product, query) {
    return this.buildSearchBlob(product).includes(query.toLowerCase());
  },

  indexEntryMatchesQuery(entry, query) {
    return entry.s.includes(query.toLowerCase());
  },

  isSubscriptionProduct(product) {
    const title = String(product.title || "").toLowerCase();
    const handle = String(product.handle || "").toLowerCase();
    const type = String(product.product_type || "").toLowerCase();
    const tags = Array.isArray(product.tags)
      ? product.tags.join(",").toLowerCase()
      : String(product.tags || "").toLowerCase();

    if (
      handle.includes("update-service") ||
      handle.includes("update_service") ||
      handle.includes("one-year") ||
      handle.includes("software-update") ||
      handle.includes("subscription")
    ) {
      return true;
    }

    if (
      title.includes("update service") ||
      title.includes("one year update") ||
      title.includes("one-year update") ||
      title.includes("one year subscription") ||
      title.includes("one-year subscription") ||
      title.includes("total care program") ||
      title.includes("software update service")
    ) {
      return true;
    }

    if (
      type.includes("software update") ||
      type.includes("update service") ||
      type.includes("subscription")
    ) {
      return true;
    }

    return (
      tags.includes("software update") ||
      tags.includes("update service") ||
      tags.includes("subscription")
    );
  },

  buildLightweightIndexEntry(product) {
    return {
      h: product.handle,
      t: this.isSubscriptionProduct(product) ? 2 : 1,
      s: this.buildSearchBlob(product),
    };
  },

  getCachedIndex() {
    try {
      const raw = sessionStorage.getItem(this.INDEX_CACHE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      if (!parsed?.items?.length || Date.now() - parsed.ts > this.INDEX_CACHE_TTL_MS) {
        return null;
      }

      return parsed.items;
    } catch {
      return null;
    }
  },

  saveCachedIndex(items) {
    try {
      sessionStorage.setItem(
        this.INDEX_CACHE_KEY,
        JSON.stringify({
          ts: Date.now(),
          items,
        }),
      );
    } catch {
      // Ignore storage quota errors.
    }
  },

  handlesFromIndex(index, query) {
    const diagnostic = [];
    const subscription = [];

    index.forEach((entry) => {
      if (!this.indexEntryMatchesQuery(entry, query)) {
        return;
      }

      if (entry.t === 2) {
        subscription.push(entry.h);
      } else {
        diagnostic.push(entry.h);
      }
    });

    return [...diagnostic, ...subscription];
  },

  sortProductsByTier(products) {
    const diagnostic = [];
    const subscription = [];

    products.forEach((product) => {
      if (this.isSubscriptionProduct(product)) {
        subscription.push(product);
      } else {
        diagnostic.push(product);
      }
    });

    return [...diagnostic, ...subscription];
  },

  async fetchProductJsonPage(page) {
    const response = await fetch(`${this.getProductsJsonBaseUrl()}?limit=250&page=${page}`);

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.products || [];
  },

  async fetchRemainingProductPagesParallel(startPage, firstBatchSize) {
    if (firstBatchSize < 250) {
      return [];
    }

    const pageNumbers = Array.from({ length: 15 }, (_, index) => startPage + index);
    const batches = await Promise.all(pageNumbers.map((page) => this.fetchProductJsonPage(page)));
    const products = [];

    batches.forEach((batch) => {
      if (batch.length) {
        products.push(...batch);
      }
    });

    return products;
  },

  async buildProductIndex(onPartialIndexReady) {
    const cachedIndex = this.getCachedIndex();
    if (cachedIndex) {
      return cachedIndex;
    }

    const firstPage = await this.fetchProductJsonPage(1);
    let products = [...firstPage];

    if (onPartialIndexReady && firstPage.length) {
      onPartialIndexReady(firstPage.map((product) => this.buildLightweightIndexEntry(product)));
    }

    const remainingProducts = await this.fetchRemainingProductPagesParallel(2, firstPage.length);
    if (remainingProducts.length) {
      products = products.concat(remainingProducts);
    }

    const index = products.map((product) => this.buildLightweightIndexEntry(product));
    this.saveCachedIndex(index);
    return index;
  },

  async fetchGridCardsForHandles(query, handles) {
    if (!handles.length) {
      return [];
    }

    const uncachedHandles = handles.filter((handle) => !this.cardCache.has(handle));

    if (uncachedHandles.length) {
      const response = await fetch(this.buildSectionUrl(query, "grid", 1, uncachedHandles));

      if (response.ok) {
        const doc = this.parseSectionHtml(await response.text());
        doc.querySelectorAll("#fuzzy-search-product-grid > li").forEach((item) => {
          const handle = item.dataset.fuzzyProductHandle;
          if (handle) {
            this.cardCache.set(handle, item.cloneNode(true));
          }
        });
      }
    }

    return handles
      .map((handle) => this.cardCache.get(handle))
      .filter(Boolean)
      .map((item) => item.cloneNode(true));
  },

  inferProductTier(item) {
    const existingTier = item.dataset.searchTier;
    if (existingTier) {
      return existingTier;
    }

    const text = (item.textContent || "").toLowerCase();
    const href = (item.querySelector('a[href*="/products/"]')?.getAttribute("href") || "").toLowerCase();
    const subscriptionPattern =
      /update service|one year update|one-year update|one year subscription|one-year subscription|total care program|software update service|update-service|one-year|subscription/;

    return subscriptionPattern.test(text) || subscriptionPattern.test(href) ? "2" : "1";
  },

  sortItemsByTier(items) {
    const diagnostic = [];
    const subscription = [];

    items.forEach((item) => {
      const tier = this.inferProductTier(item);
      item.dataset.searchTier = tier;

      if (tier === "2") {
        subscription.push(item);
      } else {
        diagnostic.push(item);
      }
    });

    return [...diagnostic, ...subscription];
  },

  async fetchPredictiveItems(query, limit = 10) {
    const items = [];
    const seenHandles = new Set();
    let page = 1;
    let hasMore = true;

    while (items.length < limit && hasMore) {
      const response = await fetch(this.buildSectionUrl(query, "predictive", page));

      if (!response.ok) {
        break;
      }

      const doc = this.parseSectionHtml(await response.text());
      const wrapper = doc.querySelector("#fuzzy-search-predictive-wrapper");

      if (!wrapper) {
        break;
      }

      doc.querySelectorAll("#fuzzy-search-results-item-product-list > li").forEach((item) => {
        const handle = item.dataset.productHandle;

        if (!handle || seenHandles.has(handle) || items.length >= limit) {
          return;
        }

        seenHandles.add(handle);
        items.push(item.cloneNode(true));
      });

      hasMore = wrapper.dataset.fuzzyHasMore === "true";
      page += 1;

      if (doc.querySelectorAll("#fuzzy-search-results-item-product-list > li").length === 0 && !hasMore) {
        break;
      }
    }

    return items;
  },

  updateProductCount(count, query) {
    const countElement = document.getElementById("ProductCount");
    if (!countElement) {
      return;
    }

    const strings = window.fuzzySearchStrings || {};
    const template = count === 1 ? strings.resultsCountOne : strings.resultsCountOther;

    if (template) {
      countElement.textContent = template.replace("@terms@", query).replace("@count@", String(count));
    }
  },

  renderPagination(currentPage, totalPages, query) {
    const container = document.getElementById("fuzzy-search-pagination");
    if (!container) {
      return;
    }

    if (totalPages <= 1) {
      container.innerHTML = "";
      return;
    }

    const strings = window.fuzzySearchStrings || {};
    let html = `<div class="pagination" data-page="${currentPage}">`;

    if (currentPage > 1) {
      html += `<a href="${this.buildPageUrl(query, currentPage - 1)}" class="page-number page-number-prev" aria-label="${strings.paginationPrevious || "Previous"}">${this.paginationIcons.previous}</a>`;
    }

    for (let page = 1; page <= totalPages; page += 1) {
      if (page === currentPage) {
        html += `<span class="page-number page-number-active">${page}</span>`;
      } else {
        html += `<a href="${this.buildPageUrl(query, page)}" class="page-number" aria-label="${(strings.paginationPage || "Page @number@").replace("@number@", String(page))}">${page}</a>`;
      }
    }

    if (currentPage < totalPages) {
      html += `<a href="${this.buildPageUrl(query, currentPage + 1)}" class="page-number page-number-next" aria-label="${strings.paginationNext || "Next"}">${this.paginationIcons.next}</a>`;
    }

    html += "</div>";
    container.innerHTML = html;

    container.querySelectorAll("a.page-number").forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        const url = new URL(link.href);
        const page = Number.parseInt(url.searchParams.get("page") || "1", 10);
        this.goToPage(page);
      });
    });
  },

  setSearchPageLoading(isLoading) {
    document.querySelector(".fuzzy-search-collection")?.classList.toggle("is-loading", isLoading);
  },

  revealSearchPosts() {
    document.getElementById("search-results-posts")?.classList.remove("is-pending");
  },

  seedCardCacheFromGrid() {
    const grid = document.getElementById("product-grid");
    if (!grid) {
      return;
    }

    grid.querySelectorAll("[data-fuzzy-product-handle]").forEach((item) => {
      const handle = item.dataset.fuzzyProductHandle;
      if (handle && !this.cardCache.has(handle)) {
        this.cardCache.set(handle, item.cloneNode(true));
      }
    });
  },

  warmProductIndex(onPartialIndexReady) {
    const cachedIndex = this.getCachedIndex();
    if (cachedIndex) {
      return Promise.resolve(cachedIndex);
    }

    if (onPartialIndexReady) {
      this.partialIndexCallbacks.push(onPartialIndexReady);
    }

    if (!this.indexBuildPromise) {
      this.indexBuildPromise = this.buildProductIndex((partialIndex) => {
        this.partialIndexCallbacks.forEach((callback) => callback(partialIndex));
      }).finally(() => {
        this.indexBuildPromise = null;
        this.partialIndexCallbacks = [];
      });
    }

    return this.indexBuildPromise;
  },

  bindSearchFormWarmup() {
    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) {
          return;
        }

        const action = form.getAttribute("action") || "";
        if (!form.classList.contains("search") && !action.includes("/search")) {
          return;
        }

        this.warmProductIndex();
      },
      true,
    );
  },

  prefetchPageCards(page) {
    if (!this.searchPageState || page < 1) {
      return;
    }

    const { handles, perPage, query } = this.searchPageState;
    const totalPages = Math.max(1, Math.ceil(handles.length / perPage));

    if (page > totalPages) {
      return;
    }

    const start = (page - 1) * perPage;
    const pageHandles = handles.slice(start, start + perPage);
    const uncachedHandles = pageHandles.filter((handle) => !this.cardCache.has(handle));

    if (!uncachedHandles.length) {
      return;
    }

    this.fetchGridCardsForHandles(query, uncachedHandles).catch(() => {});
  },

  async renderSearchPage(page, options = {}) {
    if (!this.searchPageState) {
      return;
    }

    const { handles, perPage, query } = this.searchPageState;
    const totalPages = Math.max(1, Math.ceil(handles.length / perPage));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * perPage;
    const pageHandles = handles.slice(start, start + perPage);
    const grid = document.getElementById("product-grid");

    if (!grid) {
      return;
    }

    const allCached = pageHandles.every((handle) => this.cardCache.has(handle));
    const showLoading = !options.silent && !allCached;

    if (showLoading) {
      this.setSearchPageLoading(true);
    }

    try {
      const items = await this.fetchGridCardsForHandles(query, pageHandles);

      if (items.length) {
        grid.replaceChildren(...items);
      }

      this.searchPageState.currentPage = safePage;
      this.renderPagination(safePage, totalPages, query);
      this.prefetchPageCards(safePage + 1);
    } finally {
      if (showLoading) {
        this.setSearchPageLoading(false);
      }
    }
  },

  async goToPage(page) {
    if (!this.searchPageState) {
      return;
    }

    const nextPage = Math.min(
      Math.max(page, 1),
      Math.max(1, Math.ceil(this.searchPageState.handles.length / this.searchPageState.perPage)),
    );

    history.pushState({ searchPage: nextPage }, "", this.buildPageUrl(this.searchPageState.query, nextPage));
    await this.renderSearchPage(nextPage);

    const grid = document.getElementById("product-grid");
    if (grid) {
      const top = grid.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }
  },

  mergePredictiveMarkup(nativeText, fuzzyItems) {
    if (!fuzzyItems.length) {
      return nativeText;
    }

    const doc = this.parseSectionHtml(nativeText);
    const section = doc.querySelector("#shopify-section-predictive-search");

    if (!section) {
      return nativeText;
    }

    let productList = section.querySelector("#predictive-search-results-item-product-list");
    const existingHandles = productList ? this.getExistingHandles(productList) : new Set();
    const newItems = fuzzyItems.filter((item) => {
      const handle = item.dataset.productHandle;
      return handle && !existingHandles.has(handle);
    });

    if (!newItems.length) {
      return nativeText;
    }

    if (!productList) {
      const resultsGroup = section.querySelector(".predictive-search-results-group:last-child");

      if (!resultsGroup) {
        return nativeText;
      }

      const sectionWrapper = doc.createElement("div");
      sectionWrapper.className = "predictive-search-results-group-section";

      const heading = doc.createElement("h3");
      heading.id = "predictive-search-products";
      heading.className = "predictive-search-heading predictive-search-heading-products h5";
      heading.textContent = window.fuzzySearchStrings?.productsHeading || "Products";
      sectionWrapper.appendChild(heading);

      productList = doc.createElement("ul");
      productList.id = "predictive-search-results-item-product-list";
      productList.className = "predictive-search-results-item-list";
      sectionWrapper.appendChild(productList);
      resultsGroup.prepend(sectionWrapper);

      const resultsList = section.querySelector(".predictive-search-results-list");
      if (resultsList) {
        resultsList.classList.remove("predictive-search-results-list-empty");
      }

      const groupWrapper = section.querySelector(".predictive-search-results-group-wrapper");
      if (groupWrapper) {
        groupWrapper.classList.add("predictive-search-results-group-wrapper-two-column");
      }
    }

    newItems.forEach((item, index) => {
      item.id = `predictive-search-option-fuzzy-product-${index + 1}`;
      productList.appendChild(item);
    });

    return section.outerHTML;
  },

  async initSearchPage() {
    if (!document.body.classList.contains("template-search")) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const query = (params.get("q") || "").trim();
    const grid = document.getElementById("product-grid");
    const templateSearch = document.querySelector(".template-search");
    const hasSsrProducts = grid?.dataset.hasSsr === "true";

    if (!query || !grid || query.includes(this.HANDLE_DELIMITER)) {
      return;
    }

    this.seedCardCacheFromGrid();

    const perPage = Number.parseInt(grid.dataset.perPage || "24", 10);
    const currentPage = Number.parseInt(params.get("page") || "1", 10);
    let partialHandles = null;
    let partialRenderDone = false;

    if (!hasSsrProducts) {
      this.setSearchPageLoading(true);
    }

    try {
      const cachedIndex = this.getCachedIndex();

      if (cachedIndex) {
        const handles = this.handlesFromIndex(cachedIndex, query);
        this.searchPageState = { query, perPage, handles, currentPage: 1 };

        if (!handles.length) {
          this.revealSearchPosts();
          return;
        }

        this.updateProductCount(handles.length, query);
        await this.renderSearchPage(currentPage, { silent: hasSsrProducts });
      } else {
        const indexPromise = this.warmProductIndex((partialIndex) => {
          partialHandles = this.handlesFromIndex(partialIndex, query);
          if (!partialHandles.length || partialRenderDone) {
            return;
          }

          partialRenderDone = true;
          this.searchPageState = { query, perPage, handles: partialHandles, currentPage: 1, partial: true };
          this.updateProductCount(partialHandles.length, query);
          this.setSearchPageLoading(true);
          this.renderSearchPage(currentPage, { silent: hasSsrProducts }).finally(() => {
            if (!hasSsrProducts) {
              this.setSearchPageLoading(false);
            }
          });
        });

        const index = await indexPromise;
        const handles = this.handlesFromIndex(index, query);
        this.searchPageState = { query, perPage, handles, currentPage: 1, partial: false };

        if (!handles.length) {
          this.revealSearchPosts();
          return;
        }

        this.updateProductCount(handles.length, query);

        const handlesChanged =
          !partialRenderDone ||
          (partialHandles &&
            (handles.length !== partialHandles.length ||
              handles.slice(0, perPage).join(",") !== partialHandles.slice(0, perPage).join(",")));

        if (!partialRenderDone || handlesChanged) {
          await this.renderSearchPage(currentPage, { silent: hasSsrProducts && partialRenderDone });
        } else {
          this.renderPagination(currentPage, Math.max(1, Math.ceil(handles.length / perPage)), query);
          this.prefetchPageCards(currentPage + 1);
        }
      }

      if (templateSearch?.classList.contains("template-search-empty")) {
        templateSearch.classList.remove("template-search-empty");
      }

      document.querySelector(".page-hero-empty")?.remove();
    } finally {
      this.setSearchPageLoading(false);
      this.revealSearchPosts();
    }
  },
};

window.FuzzySearch = FuzzySearch;

function bootFuzzySearch() {
  FuzzySearch.bindSearchFormWarmup();
  FuzzySearch.initSearchPage();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootFuzzySearch);
} else {
  bootFuzzySearch();
}

window.addEventListener("popstate", () => {
  if (!FuzzySearch.searchPageState) {
    return;
  }

  const page = Number.parseInt(new URLSearchParams(window.location.search).get("page") || "1", 10);
  FuzzySearch.renderSearchPage(page);
});
